import React, { useRef, useState } from 'react';
import type { Theme } from '../../components/studio/studioUi';
import { useToast } from '../../components/common/Toast';
import type { VideoRender } from '../../services/video.service';
import { socialService, type PostStatus, type ScheduledPost } from '../../services/social.service';
import { Drawer, Ico, PLATFORM_NAME, PlatformIcon, Spin, VsDialog, aspectLabel, fmtDateLong, fmtT, fmtTime, fmtWhenShort, pillBtn, vsError, POST_STATUS } from './vsUi';

/** Posts made by one "Post" submit: same render, same time, same caption (one row per account on the server). */
export interface PostGroup {
    key: string;
    posts: ScheduledPost[];
    at: Date;
    status: PostStatus;
}

/** Groups the per-account rows into what the user scheduled; newest first. Canceled rows are dropped. */
export function groupPosts(posts: ScheduledPost[]): PostGroup[] {
    const m = new Map<string, ScheduledPost[]>();
    for (const p of posts) {
        if (p.status === 'CANCELED') continue;
        const k = `${p.renderId}|${p.scheduledAt.slice(0, 16)}|${p.caption}`;
        m.set(k, [...(m.get(k) || []), p]);
    }
    return Array.from(m.entries())
        .map(([key, ps]) => ({ key, posts: ps, at: new Date(ps[0].scheduledAt), status: groupStatus(ps) }))
        .sort((a, b) => b.at.getTime() - a.at.getTime());
}

/** One status for the group: anything failed wins, then in progress, scheduled, posted. */
function groupStatus(ps: ScheduledPost[]): PostStatus {
    for (const s of ['FAILED', 'PUBLISHING', 'SCHEDULED', 'PUBLISHED'] as PostStatus[]) if (ps.some((p) => p.status === s)) return s;
    return ps[0].status;
}

const TITLE: Record<PostStatus, string> = { SCHEDULED: 'Scheduled post', PUBLISHING: 'Posting now', PUBLISHED: 'Posted', FAILED: 'Failed post', CANCELED: 'Canceled post' };

/**
 * Post detail drawer (click a row under Posts): what goes out, where and when,
 * with Cancel post (confirmed), Edit schedule and Retry for failed accounts.
 */
export const PostDetailDrawer: React.FC<{
    group: PostGroup;
    videoName: string;
    renders: VideoRender[];
    theme: Theme;
    onClose: () => void;
    onEdit: (posts: ScheduledPost[]) => void;
    onChanged: () => void;
}> = ({ group, videoName, renders, theme, onClose, onEdit, onChanged }) => {
    const toast = useToast();
    const [confirm, setConfirm] = useState(false);
    const [busy, setBusy] = useState(false);
    const first = group.posts[0];
    const render = renders.find((r) => r.id === first.renderId);
    const idx = renders.findIndex((r) => r.id === first.renderId);
    const version = idx >= 0 ? `v${renders.length - idx}` : '—';
    const format = render?.format ?? first.format;
    const dur = render?.durationSec ? fmtT(render.durationSec) : '—';
    const scheduled = group.posts.filter((p) => p.status === 'SCHEDULED');
    const failed = group.posts.filter((p) => p.status === 'FAILED');

    const run = async (fn: () => Promise<unknown>, ok: string, close = false) => {
        setBusy(true);
        try {
            await fn();
            toast.success(ok);
            onChanged();
            if (close) onClose();
        } catch (e) {
            toast.error(vsError(e));
        } finally {
            setBusy(false);
        }
    };

    const row = (label: string, value: React.ReactNode, num?: boolean) => (
        <div style={{ display: 'grid', gridTemplateColumns: '96px minmax(0,1fr)', gap: 12, alignItems: 'center', minHeight: 32 }}>
            <span style={{ fontSize: 12.5, color: 'var(--hv-body)' }}>{label}</span>
            <span style={{ fontSize: 13.5, fontVariantNumeric: num ? 'tabular-nums' : undefined }}>{value}</span>
        </div>
    );

    const footer = (scheduled.length > 0 || failed.length > 0) ? (
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px' }}>
            {scheduled.length > 0 && (
                <button type="button" className="hv-vs-under" onClick={() => setConfirm(true)} style={{ flex: 'none', whiteSpace: 'nowrap', padding: 0, border: 0, background: 'transparent', color: 'var(--hv-danger)', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', textUnderlineOffset: 3 }}>Cancel post</button>
            )}
            {failed.length > 0 && scheduled.length === 0 && (
                <button type="button" className="hv-vs-under" disabled={busy} onClick={() => run(() => Promise.all(failed.map((p) => socialService.cancel(p.id))), 'Removed from the list.', true)} style={{ flex: 'none', whiteSpace: 'nowrap', padding: 0, border: 0, background: 'transparent', color: 'var(--hv-danger)', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>Remove</button>
            )}
            <div style={{ flex: 1 }} />
            {failed.length > 0 && (
                <button type="button" className="hv-vs-bd" disabled={busy} onClick={() => run(() => Promise.all(failed.map((p) => socialService.retry(p.id))), 'Retrying now.')} style={{ flex: 'none', whiteSpace: 'nowrap', height: 36, padding: '0 18px', borderRadius: 999, border: '1px solid var(--hv-line-2)', background: 'transparent', color: 'var(--hv-ink)', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>{busy && <Spin />}Retry</button>
            )}
            {scheduled.length > 0 && (
                <button type="button" className="hv-vs-bd" onClick={() => onEdit(scheduled)} style={{ flex: 'none', whiteSpace: 'nowrap', height: 36, padding: '0 18px', borderRadius: 999, border: '1px solid var(--hv-line-2)', background: 'transparent', color: 'var(--hv-ink)', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>Edit schedule</button>
            )}
        </div>
    ) : null;

    return (
        <>
            <Drawer title={TITLE[group.status]} label={TITLE[group.status]} width={440} onClose={onClose} theme={theme} z={8900} footer={footer}>
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 24px 24px', display: 'flex', flexDirection: 'column', gap: 22 }}>
                    <div style={{ height: 220, flex: 'none', borderRadius: 12, background: 'var(--hv-page-2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Thumb url={render?.url ?? null} ratio={format === 'landscape' ? '16 / 9' : format === 'square' ? '1 / 1' : '9 / 16'} dur={dur} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {row('Name', videoName)}
                        {row('Version', version)}
                        {row('Status', POST_STATUS[group.status])}
                        {row('Aspect ratio', aspectLabel(format) || '—')}
                        {row('Duration', dur, true)}
                        {row('Date', fmtDateLong(group.at))}
                        {row('Time', fmtTime(group.at), true)}
                        <div style={{ display: 'grid', gridTemplateColumns: '96px minmax(0,1fr)', gap: 12, alignItems: 'start', padding: '4px 0' }}>
                            <span style={{ fontSize: 12.5, color: 'var(--hv-body)', paddingTop: 6 }}>{group.posts.length > 1 ? 'Accounts' : 'Account'}</span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
                                {group.posts.map((p) => (
                                    <div key={p.id} style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                            <PlatformIcon platform={p.platform} size={22} />
                                            <span style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                                <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 13.5 }}>{p.account.name}</span>
                                                <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12, color: 'var(--hv-body)' }}>
                                                    {PLATFORM_NAME[p.platform]}{p.options?.placement === 'story' ? ' · Story' : ''}{group.posts.length > 1 ? ` · ${POST_STATUS[p.status]}` : ''}
                                                </span>
                                            </span>
                                            {p.externalUrl && <a href={p.externalUrl} target="_blank" rel="noreferrer" title="View post" style={{ flex: 'none', display: 'flex', color: 'var(--hv-brand-ink)' }}>{Ico.external()}</a>}
                                        </span>
                                        {p.status === 'FAILED' && p.error && <span style={{ paddingLeft: 32, fontSize: 12, lineHeight: 1.5, color: 'var(--hv-danger)', overflowWrap: 'anywhere' }}>{p.error}</span>}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '96px minmax(0,1fr)', gap: 12, alignItems: 'start', padding: '7px 0' }}>
                            <span style={{ fontSize: 12.5, color: 'var(--hv-body)' }}>Caption</span>
                            <span style={{ fontSize: 13.5, lineHeight: 1.55, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{first.caption || '—'}</span>
                        </div>
                    </div>
                </div>
            </Drawer>
            {confirm && (
                <VsDialog title="Cancel this post?" alert theme={theme} onClose={() => setConfirm(false)} z={9200}>
                    <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: 'var(--hv-ink)' }}>
                        The post on {fmtWhenShort(group.at)} won't go out and is removed from the Scheduler. The video stays in your project.
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                        <button type="button" className="hv-vs-ghost" onClick={() => setConfirm(false)} style={{ ...pillBtn(false), height: 34 }}>Keep post</button>
                        <button
                            type="button"
                            className="hv-vs-danger"
                            disabled={busy}
                            onClick={() => { setConfirm(false); run(() => Promise.all(scheduled.map((p) => socialService.cancel(p.id))), 'Post canceled.', true); }}
                            style={{ height: 34, padding: '0 14px', border: 0, borderRadius: 999, background: 'var(--hv-danger)', color: '#FFFDF7', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}
                        >
                            Cancel post
                        </button>
                    </div>
                </VsDialog>
            )}
        </>
    );
};

/** Still frame of the render with a play badge; plays inline on click. */
const Thumb: React.FC<{ url: string | null; ratio: string; dur: string }> = ({ url, ratio, dur }) => {
    const ref = useRef<HTMLVideoElement>(null);
    const [playing, setPlaying] = useState(false);
    return (
        <div style={{ position: 'relative', height: 148, aspectRatio: ratio, maxWidth: '80%', borderRadius: 8, overflow: 'hidden', background: 'var(--hv-sand)', border: '1px solid var(--hv-line)', boxShadow: '0 14px 30px -18px rgba(0,0,0,0.4)' }}>
            {url && <video ref={ref} src={`${url}#t=0.5`} muted playsInline preload="metadata" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
            <button type="button" aria-label={playing ? 'Pause' : 'Play'} onClick={() => { const v = ref.current; if (v) { if (v.paused) v.play().catch(() => {}); else v.pause(); } }} style={{ position: 'absolute', inset: 0, border: 0, padding: 0, background: 'transparent', cursor: url ? 'pointer' : 'default' }}>
                {!playing && (
                    <span style={{ position: 'absolute', left: '50%', top: '50%', width: 40, height: 40, margin: '-20px 0 0 -20px', borderRadius: 999, background: 'rgba(255,253,247,0.92)', boxShadow: '0 6px 16px -6px rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--brand,#13422C)' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: 2 }}><path d="M7 4.5v15l12.5-7.5z" /></svg>
                    </span>
                )}
            </button>
            <span style={{ position: 'absolute', right: 8, bottom: 8, height: 20, padding: '0 7px', borderRadius: 999, display: 'flex', alignItems: 'center', background: 'rgba(20,24,22,0.72)', color: '#fff', fontSize: 11, fontWeight: 600, fontVariantNumeric: 'tabular-nums', pointerEvents: 'none' }}>{dur}</span>
        </div>
    );
};
